import axios from "axios";
import { getCookie } from "../../../config/cookie";

// API layer for cloud storage providers (Google Drive, OneDrive, ...).
// All endpoints are provider-agnostic: pass the provider id from
// utils/cloudStorageProviders.js and the backend routes to the right
// CloudStorageProvider implementation.

const BASE = `${process.env.REACT_APP_BASE_PATH}/chats/cloud-storage`;

const headerRequest = () => ({
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${getCookie("token")}`,
  },
});

// [{provider, label, configured, connected, accountEmail, accountName}]
export const getCloudStorageProviders = async () => {
  const response = await axios.get(`${BASE}/providers`, headerRequest());
  return response?.data?.data;
};

// {url} — OAuth consent URL to open in a popup
export const getCloudStorageAuthUrl = async (providerId) => {
  const response = await axios.get(
    `${BASE}/${providerId}/auth-url`,
    headerRequest()
  );
  return response?.data?.data;
};

export const disconnectCloudStorage = async (providerId) => {
  const response = await axios.delete(`${BASE}/${providerId}`, headerRequest());
  return response?.data?.data;
};

// {files: [...], nextPageToken} — omit folderId for the root folder; pass
// search to search instead of browsing; view switches collection ("recent",
// "shared", "shared-drives", ..., per provider — see
// utils/cloudStorageProviders.js); driveId is required once browsing inside
// an item outside the user's own default drive (carry it forward from that
// file/folder's `driveId` field); pageSize caps the result count (e.g. a
// small value for a live search-suggestions dropdown).
export const listCloudStorageFiles = async (
  providerId,
  { folderId, driveId, search, view, pageToken, pageSize } = {}
) => {
  const response = await axios.get(`${BASE}/${providerId}/files`, {
    ...headerRequest(),
    params: {
      folderId: folderId || undefined,
      driveId: driveId || undefined,
      search: search || undefined,
      view: view || undefined,
      pageToken: pageToken || undefined,
      pageSize: pageSize || undefined,
    },
  });
  return response?.data?.data;
};

// {url, mimeType} — url is safe to drop straight into an <img>/<iframe> src
// to preview a file that's still in the provider (not yet imported into
// Arkchat storage); see FilePreview.jsx. driveId is required for the same
// reason it is on listCloudStorageFiles above.
export const getCloudStoragePreviewUrl = async (
  providerId,
  { fileId, driveId } = {}
) => {
  const response = await axios.get(`${BASE}/${providerId}/preview-url`, {
    ...headerRequest(),
    params: {
      fileId,
      driveId: driveId || undefined,
    },
  });
  return response?.data?.data;
};

// Imports copies of the files into Arkchat storage; returns the same
// attachment objects ({location, name, mimeType}) as the file upload API.
export const importCloudStorageFiles = async (providerId, pnId, files) => {
  const response = await axios.post(
    `${BASE}/${providerId}/import/${pnId}`,
    {
      files: files.map((file) => ({
        id: file.id,
        name: file.name,
        driveId: file.driveId || undefined,
      })),
    },
    headerRequest()
  );
  return response?.data?.data;
};
